import { OrgData } from '@rolebase/shared/model/OrgData'
import { RoleLink } from '@rolebase/shared/model/roleLinks'
import { zoomIdentity } from 'd3'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LinkNode, layoutRoleLinks } from '../core/layoutRoleLinks'
import { getColor } from '../helpers/colors'
import { GraphColorMode, GraphEvents, Position } from '../types'
import { useZoomPan } from './hooks/useZoomPan'

export interface RoleLinksViewProps {
  org: OrgData
  // Roles citing each other, read from their texts (extractRoleLinks)
  links: readonly RoleLink[]
  width: number
  height: number
  colorMode: GraphColorMode
  events: GraphEvents
  selectedCircleId?: string
  focusCrop?: Position
  // Shown when no role cites another one
  emptyLabel: string
}

const MIN_SCALE = 0.2
const MAX_SCALE = 2
// One colour for every citation (Yohan, 22/09), the accent of the org chart,
// drawn thin so the web reads before its arrows
const CITATION_COLOR = '#f24911'
const CITATION_WIDTH = 1.2
const CITATION_OPACITY = 0.75
// Citations bow to one side, the same amount for every arrow: A → B and B → A
// then draw two arcs instead of one line carrying two heads
const CITATION_BOW = 0.14
// A press that travelled less than this many pixels is a click, not a drag
const DRAG_SLOP = 4

// Links view (#107, phase 4): a web of the roles citing each other with "#",
// the circles holding them as a skeleton. Its own zoom and pan
export default function RoleLinksView({
  org,
  links,
  width,
  height,
  colorMode,
  events,
  selectedCircleId,
  focusCrop,
  emptyLabel,
}: RoleLinksViewProps) {
  const layout = useMemo(() => layoutRoleLinks(org, links), [org, links])
  const open = useCallback((id: string) => events.onCircleClick?.(id), [events])

  // Nodes can be dragged: the web is computed once, then it is the reader's to
  // arrange. A move lives as long as the view is open
  const [moved, setMoved] = useState<Record<string, { x: number; y: number }>>(
    {}
  )
  useEffect(() => setMoved({}), [layout])
  const scaleRef = useRef(1)
  const place = useCallback(
    (node: { id: string; x: number; y: number }) => moved[node.id] ?? node,
    [moved]
  )
  const handleDrag = useCallback(
    (id: string, from: { x: number; y: number }, event: React.PointerEvent) => {
      const start = { x: event.clientX, y: event.clientY }
      const target = event.currentTarget as HTMLElement
      target.setPointerCapture(event.pointerId)
      const move = (moveEvent: PointerEvent) =>
        setMoved((current) => ({
          ...current,
          [id]: {
            x: from.x + (moveEvent.clientX - start.x) / scaleRef.current,
            y: from.y + (moveEvent.clientY - start.y) / scaleRef.current,
          },
        }))
      const stop = () => {
        target.removeEventListener('pointermove', move)
        target.removeEventListener('pointerup', stop)
        target.removeEventListener('pointercancel', stop)
      }
      target.addEventListener('pointermove', move)
      target.addEventListener('pointerup', stop)
      target.addEventListener('pointercancel', stop)
    },
    []
  )

  // Opens centred on the web, shrunk to fit but never below MIN_SCALE
  const visibleWidth = width - (focusCrop?.left ?? 0) - (focusCrop?.right ?? 0)
  const { containerRef, transform } = useZoomPan(
    {
      scaleExtent: [MIN_SCALE, MAX_SCALE],
      // A press on a node drags that node, and must not pan the web with it.
      // The zoom listens on the container, below React's own root listener, so
      // it fires first: stopping the event in a React handler would be too
      // late. It is refused here instead
      filter: (event: Event) => {
        const target = event.target as HTMLElement | null
        return !target?.closest?.('[data-rb-link-node]')
      },
      fit: () => {
        if (layout.nodes.length === 0) return undefined
        // Centred on the web itself, not on the origin: the forces settle it
        // wherever they like
        const xs = layout.nodes.flatMap((node) => [
          node.x - node.r,
          node.x + node.r,
        ])
        const ys = layout.nodes.flatMap((node) => [
          node.y - node.r,
          node.y + node.r,
        ])
        const [minX, maxX] = [Math.min(...xs), Math.max(...xs)]
        const [minY, maxY] = [Math.min(...ys), Math.max(...ys)]
        const k = Math.max(
          MIN_SCALE,
          Math.min(
            1,
            visibleWidth / (maxX - minX + 120),
            height / (maxY - minY + 120)
          )
        )
        return zoomIdentity
          .translate((focusCrop?.left ?? 0) + visibleWidth / 2, height / 2)
          .scale(k)
          .translate(-(minX + maxX) / 2, -(minY + maxY) / 2)
      },
    },
    [visibleWidth, height, layout]
  )
  scaleRef.current = transform.k

  const handleClick = (event: React.MouseEvent) => {
    if (event.target === containerRef.current) events.onClickOutside?.()
  }

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      style={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        cursor: 'grab',
        touchAction: 'none',
      }}
    >
      {layout.nodes.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            color: colorMode === 'light' ? '#4b5563' : '#cbd5e0',
          }}
        >
          {emptyLabel}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transformOrigin: '0 0',
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
          pointerEvents: 'none',
        }}
      >
        <svg
          width="1"
          height="1"
          style={{ position: 'absolute', overflow: 'visible' }}
          aria-hidden="true"
        >
          <defs>
            <marker
              id="rb-citation-arrow"
              opacity={CITATION_OPACITY}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              markerUnits="userSpaceOnUse"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 z" fill={CITATION_COLOR} />
            </marker>
          </defs>
          {layout.edges.map((edge) => {
            const citation = edge.kind === 'citation'
            const from = place({ id: edge.fromId, x: edge.x1, y: edge.y1 })
            const to = place({ id: edge.toId, x: edge.x2, y: edge.y2 })
            const [dx, dy] = [to.x - from.x, to.y - from.y]
            const length = Math.hypot(dx, dy) || 1
            // A citation stops short of the disc it points to, so its head
            // stays visible instead of sliding under it
            const back = citation ? 24 : 0
            const [x2, y2] = [
              to.x - (dx / length) * back,
              to.y - (dy / length) * back,
            ]
            const color = citation
              ? CITATION_COLOR
              : colorMode === 'light'
              ? 'hsl(200 10% 75%)'
              : 'hsl(200 10% 40%)'
            if (!citation) {
              return (
                <line
                  key={edge.id}
                  x1={from.x}
                  y1={from.y}
                  x2={x2}
                  y2={y2}
                  stroke={color}
                  strokeWidth={1}
                />
              )
            }
            // Bowed to the left of its own direction: the way back bows the
            // other way, and the two never overlap
            const [mx, my] = [(from.x + x2) / 2, (from.y + y2) / 2]
            const [cx, cy] = [
              mx - (y2 - from.y) * CITATION_BOW,
              my + (x2 - from.x) * CITATION_BOW,
            ]
            return (
              <path
                key={edge.id}
                d={`M${from.x},${from.y} Q${cx},${cy} ${x2},${y2}`}
                fill="none"
                stroke={color}
                strokeOpacity={CITATION_OPACITY}
                strokeWidth={CITATION_WIDTH}
                markerEnd="url(#rb-citation-arrow)"
              />
            )
          })}
        </svg>
        {layout.nodes.map((node) => (
          <Node
            key={node.id}
            node={node}
            at={place(node)}
            colorMode={colorMode}
            selected={node.id === selectedCircleId}
            onOpen={open}
            onDrag={handleDrag}
          />
        ))}
      </div>
    </div>
  )
}

const Node = memo(function Node({
  node,
  at,
  colorMode,
  selected,
  onOpen,
  onDrag,
}: {
  node: LinkNode
  // Where it sits now: its place in the web, or where it was dragged to
  at: { x: number; y: number }
  colorMode: GraphColorMode
  selected: boolean
  onOpen(id: string): void
  onDrag(
    id: string,
    from: { x: number; y: number },
    event: React.PointerEvent
  ): void
}) {
  const depth = node.kind === 'circle' ? 1 : 2
  const hue = node.hue ?? undefined
  // A drag must not open the node it moved: a press that travelled more than
  // DRAG_SLOP is a move, not a click
  const pressedAt = useRef<{ x: number; y: number } | null>(null)
  const dragged = useRef(false)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onOpen(node.id)
  }
  return (
    <div
      data-rb-link-node=""
      role="button"
      tabIndex={0}
      aria-label={node.name}
      onKeyDown={handleKeyDown}
      onClick={(event) => {
        event.stopPropagation()
        if (dragged.current) return
        onOpen(node.id)
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        event.stopPropagation()
        pressedAt.current = { x: event.clientX, y: event.clientY }
        dragged.current = false
        onDrag(node.id, at, event)
      }}
      onPointerMove={(event) => {
        const pressed = pressedAt.current
        if (!pressed) return
        const travel = Math.hypot(
          event.clientX - pressed.x,
          event.clientY - pressed.y
        )
        if (travel > DRAG_SLOP) dragged.current = true
      }}
      onPointerUp={() => {
        pressedAt.current = null
      }}
      title={node.name}
      style={{
        position: 'absolute',
        left: at.x - node.r,
        top: at.y - node.r,
        width: node.r * 2,
        height: node.r * 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        borderRadius: '50%',
        background: getColor(colorMode, 94, 16, depth, hue),
        boxShadow: selected
          ? `0 0 0 3px ${getColor(colorMode, 75, 35, depth, hue)}`
          : undefined,
        cursor: 'grab',
        pointerEvents: 'auto',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      {/* The name under the disc: a role's disc is too small to hold it, and a
          truncated name names nothing */}
      <span
        style={{
          position: 'absolute',
          top: '100%',
          marginTop: 2,
          width: 120,
          color: colorMode === 'light' ? '#1f2937' : '#f7fafc',
          fontSize: node.kind === 'circle' ? 12 : 10,
          fontWeight: node.kind === 'circle' ? 700 : 500,
          lineHeight: 1.15,
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        {node.name}
      </span>
    </div>
  )
})
